import { addDays, differenceInCalendarDays, isEqual, isSameDay, isWithinInterval, subDays } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { useFormikContext } from "formik";
import useDidUpdate from "hooks/useDidUpdate";
import useToast from "hooks/useToast";
import { first, last } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { retrieveContactTimezone } from "store/features/localization.store";
import { TOAST_TYPES } from "../../../constants";
import { generateDropTimeOptions, generatePickUpTimeOptions } from "../utils/helper";

const useTimeForm = (withoutPastLimit = false) => {
  const { t } = useTranslation();
  const onShowToast = useToast();

  const {
    values: { startDate, endDate, isExpress },
    setFieldValue,
  } = useFormikContext();

  const timezone = useSelector(retrieveContactTimezone);

  const timePickUpOptions = useMemo(() => {
    return generatePickUpTimeOptions({ startDate, isExpress, withoutPastLimit, timezone });
  }, [startDate, isExpress, withoutPastLimit, timezone]);

  const timeToDropOffOptions = useMemo(() => {
    return generateDropTimeOptions({ startDate, endDate, isExpress, withoutPastLimit, timezone });
  }, [startDate, endDate, isExpress, withoutPastLimit, timezone]);

  const onToggleIsExpress = useCallback(
    (newValue) => {
      setFieldValue("isExpress", newValue);

      if (newValue || !startDate || !endDate) return;

      const [sd, ed] = [utcToZonedTime(startDate, timezone), utcToZonedTime(endDate, timezone)];

      let newEndDate = ed;

      if (sd.getHours() >= 19) isSameDay(sd, ed) && (newEndDate = addDays(newEndDate, 1));
      else !isSameDay(sd, ed) && (newEndDate = subDays(newEndDate, 1));

      setFieldValue("endDate", zonedTimeToUtc(newEndDate, timezone).toISOString());
    },
    [startDate, endDate, setFieldValue, timezone]
  );

  const onChangePickUpAt = useCallback(
    (_, newStartDate) => {
      setFieldValue("startDate", newStartDate);

      newStartDate = utcToZonedTime(newStartDate, timezone);

      let newEndDate = utcToZonedTime(endDate, timezone);
      const lastEndTimeOption = utcToZonedTime(last(timeToDropOffOptions).value, timezone);

      if (!isExpress) {
        if (newStartDate.getHours() >= 19) {
          isSameDay(newStartDate, newEndDate) && (newEndDate = addDays(lastEndTimeOption, 1));
        } else {
          !isSameDay(newStartDate, newEndDate) && (newEndDate = subDays(lastEndTimeOption, 1));
        }
      }

      if (isEqual(newEndDate, zonedTimeToUtc(endDate, timezone))) return;
      setFieldValue("endDate", zonedTimeToUtc(newEndDate, timezone).toISOString());
    },
    [endDate, timeToDropOffOptions, isExpress, setFieldValue, timezone]
  );

  const onChangeDropOffAt = useCallback((_, newValue) => setFieldValue("endDate", newValue), [setFieldValue]);

  useEffect(() => {
    if (!endDate || !timeToDropOffOptions.length) return;

    const firstDropOffOption = utcToZonedTime(first(timeToDropOffOptions).value, timezone);
    const lastDropOffOption = utcToZonedTime(last(timeToDropOffOptions).value, timezone);

    if (isWithinInterval(utcToZonedTime(endDate, timezone), { start: firstDropOffOption, end: lastDropOffOption }))
      return;

    onShowToast(t("common.forms.dropOffTimeToast"), { type: TOAST_TYPES.WARNING, timeout: 2000 });
    setFieldValue("endDate", last(timeToDropOffOptions).value);
  }, [endDate, timeToDropOffOptions, setFieldValue, onShowToast, timezone]);

  useEffect(() => {
    if (!startDate || !timePickUpOptions.length) return;

    const firstPickUpOption = utcToZonedTime(first(timePickUpOptions).value, timezone);
    const lastPickUpOption = utcToZonedTime(last(timePickUpOptions).value, timezone);

    if (isWithinInterval(utcToZonedTime(startDate, timezone), { start: firstPickUpOption, end: lastPickUpOption }))
      return;

    onShowToast(t("common.forms.pickUpTimeToast"), { type: TOAST_TYPES.WARNING, timeout: 2000 });
    setFieldValue("startDate", first(timePickUpOptions).value);
  }, [startDate, timePickUpOptions, onShowToast, setFieldValue, timezone]);

  useDidUpdate(
    (prev, curr) => {
      if (
        !!prev.endDate &&
        !!curr.endDate &&
        differenceInCalendarDays(utcToZonedTime(prev.endDate, timezone), utcToZonedTime(curr.endDate, timezone))
      ) {
        onShowToast(t("common.forms.dropOffTimeToast"), { type: TOAST_TYPES.WARNING, timeout: 2000 });
      }
    },
    { endDate, onShowToast }
  );

  return [
    { timePickUpOptions, timeToDropOffOptions },
    { onToggleIsExpress, onChangePickUpAt, onChangeDropOffAt },
  ];
};

export default useTimeForm;
